import { Injectable } from '@angular/core'
import { environment } from '@employer/environments/environment'
import { isNotNil } from '@engineering11/utility'
import { RestApiClient } from '@engineering11/web-api-rest'
import { Store } from '@ngrx/store'
import { filter, map, Observable } from 'rxjs'
import { getBaseSuffix } from 'shared-lib'
import { selectors } from '../store/selectors'

/** sessionStorage key holding the switched-into client's display name, for the client-switch banner. */
export const CLIENT_SWITCH_LABEL_KEY = 'employer.clientSwitchLabel'

/**
 * Exchanges the TenantAdmin's selected client (`targetCustomerKey =
 * client.clientCustomerKey`, the provisioned client's customer account) for a
 * Firebase custom token scoped to that client, so the app can re-sign in via the
 * same `customTokenSignIn` → `OnLogIn` flow the act-as handoff uses.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientSwitchService {
  private restApiClient: RestApiClient

  constructor(private store: Store) {
    this.restApiClient = new RestApiClient({
      baseUrl: environment.services.user,
      token: store.select(selectors.getCurrentToken).pipe(filter(isNotNil)),
      baseSuffix: getBaseSuffix(environment, 'user'),
    })
  }

  switchClient(targetCustomerKey: string): Observable<{ token: string }> {
    return this.restApiClient.post<{ token: string }>('client-switch', { targetCustomerKey }).pipe(map(res => res.data))
  }

  /**
   * Returns a switched-client session to its tenant. The backend reads the `switchedFrom` scope off
   * the caller's token and mints a token back to that tenant — no body needed. The app then re-signs
   * in with it via the same `customTokenSignIn` → `OnLogIn` flow the forward switch uses.
   */
  returnToTenant(): Observable<{ token: string }> {
    return this.restApiClient.post<{ token: string }>('client-switch/return', {}).pipe(map(res => res.data))
  }
}
